import React, { useState, useEffect } from 'react'
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  UserGroupIcon,
  SparklesIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { memberLevelsApi } from '../../services/adminApi'

interface MemberLevel {
  id: number
  name: string
  name_en: string | null
  icon: string
  color: string
  sort_order: number
  description: string | null
  is_active: number
  created_at: string
  updated_at: string
}

export default function MemberLevelManagement() {
  const [levels, setLevels] = useState<MemberLevel[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingLevel, setEditingLevel] = useState<MemberLevel | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    name_en: '',
    icon: '👤',
    color: '#6B7280',
    sort_order: 0,
    description: ''
  })

  // 加载会员等级列表
  const loadLevels = async () => {
    setLoading(true)
    try {
      const response = await memberLevelsApi.getAll()
      if (response.success) {
        setLevels(response.data || [])
      }
    } catch (error) {
      console.error('加载会员等级失败:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadLevels()
  }, [])

  // 打开新建/编辑弹窗
  const openModal = (level?: MemberLevel) => {
    if (level) {
      setEditingLevel(level)
      setFormData({
        name: level.name,
        name_en: level.name_en || '',
        icon: level.icon,
        color: level.color,
        sort_order: level.sort_order,
        description: level.description || ''
      })
    } else {
      setEditingLevel(null)
      setFormData({
        name: '',
        name_en: '',
        icon: '👤',
        color: '#6B7280',
        sort_order: levels.length + 1,
        description: ''
      })
    }
    setShowModal(true)
  }

  // 保存会员等级
  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('请输入等级名称')
      return
    }

    try {
      if (editingLevel) {
        const response = await memberLevelsApi.update(editingLevel.id, formData)
        if (response.success) {
          toast.success('会员等级更新成功')
          setShowModal(false)
          loadLevels()
        } else {
          toast.error(response.error?.message || '更新失败')
        }
      } else {
        const response = await memberLevelsApi.create(formData)
        if (response.success) {
          toast.success('会员等级创建成功')
          setShowModal(false)
          loadLevels()
        } else {
          toast.error(response.error?.message || '创建失败')
        }
      }
    } catch (error) {
      console.error('保存会员等级失败:', error)
      toast.error('操作失败，请重试')
    }
  }

  // 删除会员等级
  const handleDelete = async (level: MemberLevel) => {
    if (!confirm(`确定要删除会员等级"${level.name}"吗？`)) return

    try {
      const response = await memberLevelsApi.delete(level.id)
      if (response.success) {
        toast.success('会员等级删除成功')
        loadLevels()
      } else {
        toast.error(response.error?.message || '删除失败')
      }
    } catch (error) {
      console.error('删除会员等级失败:', error)
      toast.error('删除失败，请重试')
    }
  }

  // 切换启用状态
  const toggleActive = async (level: MemberLevel) => {
    try {
      const response = await memberLevelsApi.update(level.id, {
        is_active: level.is_active ? 0 : 1
      })
      if (response.success) {
        toast.success(level.is_active ? '已禁用' : '已启用')
        loadLevels()
      }
    } catch (error) {
      toast.error('操作失败')
    }
  }

  // 常用图标选项
  const iconOptions = ['👤', '🥈', '🥇', '💎', '👑', '⭐', '🌟', '💫', '🎖️', '🏆']
  
  // 颜色选项
  const colorOptions = [
    '#6B7280', '#9CA3AF', '#F59E0B', '#8B5CF6', 
    '#EC4899', '#10B981', '#3B82F6', '#EF4444'
  ]

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <SparklesIcon className="w-8 h-8 text-purple-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">会员等级管理</h1>
            <p className="text-sm text-gray-500">管理学生会员等级，可为学生设置不同等级</p>
          </div>
        </div>
        <button
          onClick={() => openModal()}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          <PlusIcon className="w-5 h-5" />
          新建等级
        </button>
      </div>

      {/* 等级列表 */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        </div>
      ) : levels.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <UserGroupIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p>暂无会员等级，点击上方按钮创建</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {levels.map((level) => (
            <div
              key={level.id}
              className={`bg-white rounded-xl border-2 p-4 transition-all hover:shadow-lg ${
                level.is_active ? 'border-gray-200' : 'border-gray-100 opacity-60'
              }`}
              style={{ borderLeftColor: level.color, borderLeftWidth: '4px' }}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{level.icon}</span>
                  <div>
                    <h3 className="font-semibold text-gray-900">{level.name}</h3>
                    {level.name_en && (
                      <p className="text-xs text-gray-500">{level.name_en}</p>
                    )}
                  </div>
                </div>
                <div
                  className="w-6 h-6 rounded-full"
                  style={{ backgroundColor: level.color }}
                ></div>
              </div>
              
              {level.description && (
                <p className="text-sm text-gray-600 mb-3">{level.description}</p>
              )}
              
              <div className="flex items-center justify-between pt-3 border-t">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => toggleActive(level)}
                    className={`text-xs px-2 py-1 rounded ${
                      level.is_active 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {level.is_active ? '启用中' : '已禁用'}
                  </button>
                  <span className="text-xs text-gray-400">排序: {level.sort_order}</span>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => openModal(level)}
                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(level)}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 新建/编辑弹窗 */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden">
            <div className="px-6 py-4 border-b bg-purple-50">
              <h2 className="text-lg font-semibold text-purple-900">
                {editingLevel ? '编辑会员等级' : '新建会员等级'}
              </h2>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  等级名称 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="如：金卡会员"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  英文名称
                </label>
                <input
                  type="text"
                  value={formData.name_en}
                  onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="如：Gold"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  图标
                </label>
                <div className="flex flex-wrap gap-2">
                  {iconOptions.map((icon) => (
                    <button
                      key={icon}
                      type="button"
                      onClick={() => setFormData({ ...formData, icon })}
                      className={`w-10 h-10 text-xl rounded-lg border-2 transition-all ${
                        formData.icon === icon
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-purple-300'
                      }`}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  颜色
                </label>
                <div className="flex flex-wrap gap-2">
                  {colorOptions.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setFormData({ ...formData, color })}
                      className={`w-8 h-8 rounded-full border-2 transition-all ${
                        formData.color === color
                          ? 'border-gray-900 scale-110'
                          : 'border-transparent hover:scale-105'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  排序
                </label>
                <input
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  min="0"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  描述
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  rows={2}
                  placeholder="可选，描述该等级的特点或权益"
                />
              </div>
            </div>
            
            <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                保存
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

